import { AlertCircle, Check, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useT } from '@/i18n/locale';
import { cn } from '@/lib/utils';

import { passwordChecks } from './password';

/*
 * Form primitives for the threshold screens (web/docs/ui-ux.md §8, §11): a
 * visible <label> per field, a persistent hint when the field has rules,
 * errors as text beside the field with role="alert" (announced, never
 * colour-only), and the single amber action per view is the submit — with a
 * spinner while in flight.
 */

export interface AuthFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string | null;
  /** Quiet action rendered on the label row (e.g. "Forgot password?"). */
  labelTrailing?: ReactNode;
  /** Rendered under the input and wired into aria-describedby. */
  hint?: ReactNode;
  /** Rendered inside the input's right edge (e.g. the show-password toggle). */
  trailing?: ReactNode;
}

export function AuthField({
  id,
  label,
  error,
  labelTrailing,
  hint,
  trailing,
  className,
  ...props
}: AuthFieldProps) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null]
    .filter(Boolean)
    .join(' ');
  // The label-row action sits AFTER the input in the DOM (grid places it on
  // the label row), so Tab goes field → field → action, never through
  // "Forgot password?" between the email and the password.
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2 gap-y-2">
      <Label htmlFor={id} className="col-start-1 row-start-1">
        {label}
      </Label>
      <div className="relative col-span-2 row-start-2">
        <Input
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={cn(trailing ? 'pr-12' : null, className)}
          {...props}
        />
        {trailing ? (
          <div className="absolute inset-y-0 right-1 flex items-center">{trailing}</div>
        ) : null}
      </div>
      {labelTrailing ? <div className="col-start-2 row-start-1 justify-self-end">{labelTrailing}</div> : null}
      {error ? (
        <p
          id={`${id}-error`}
          role="alert"
          className="col-span-2 flex items-start gap-1.5 text-caption text-danger"
        >
          <AlertCircle className="mt-px h-4 w-4 shrink-0" strokeWidth={1.75} aria-hidden="true" />
          {error}
        </p>
      ) : null}
      {hint ? (
        <div id={`${id}-hint`} className="col-span-2">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A password input with a show/hide toggle. Replaces the old "confirm
 * password" second field: letting people see what they typed catches typos
 * with one field instead of two.
 */
export function PasswordField(props: Omit<AuthFieldProps, 'type' | 'trailing'>) {
  const [visible, setVisible] = useState(false);
  const t = useT();
  return (
    <AuthField
      {...props}
      type={visible ? 'text' : 'password'}
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      trailing={
        <button
          type="button"
          className="touch-target-min inline-flex h-10 w-10 items-center justify-center rounded text-ink-secondary transition-colors duration-150 hover:bg-surface-alt hover:text-ink"
          aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}
          aria-pressed={visible}
          aria-controls={props.id}
          onClick={() => setVisible((value) => !value)}
          disabled={props.disabled}
        >
          {visible ? (
            <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
          ) : (
            <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden="true" />
          )}
        </button>
      }
    />
  );
}

/**
 * The live password checklist shown under a new-password field. Each rule
 * turns `success` as it is met; the met/unmet state is also in text for
 * screen readers, never colour-only.
 */
export function PasswordRequirements({ value }: { value: string }) {
  const t = useT();
  const checks = passwordChecks(value);
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-caption text-ink-secondary">{t('auth.pw.title')}</p>
      <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        {checks.map((check) => (
          <li
            key={check.id}
            className={cn(
              'flex items-center gap-2 text-caption transition-colors duration-150',
              check.met ? 'text-success' : 'text-ink-secondary',
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors duration-150',
                check.met ? 'bg-success text-bg' : 'border border-border',
              )}
            >
              {check.met ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
            </span>
            {t(`auth.pw.${check.id}`)}
            <span className="sr-only">
              {' '}
              ({check.met ? t('auth.pw.met') : t('auth.pw.unmet')})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The one amber action on an auth view. */
export function SubmitButton({ busy, children }: { busy: boolean; children: ReactNode }) {
  return (
    <Button type="submit" variant="accent" size="lg" disabled={busy} className="mt-1 w-full">
      {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : null}
      {children}
    </Button>
  );
}

/** Form-level error (wrong credentials, network, server) — a tinted block, not just red text. */
export function FormAlert({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2.5 rounded bg-danger-soft px-3.5 py-3 text-subhead text-ink"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" strokeWidth={2} aria-hidden="true" />
      <div>{children}</div>
    </div>
  );
}

/** "or use email" — a labelled rule between the social buttons and the form. */
export function OrDivider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3" role="separator" aria-label={String(children)}>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
      <span className="text-caption text-ink-secondary" aria-hidden="true">
        {children}
      </span>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  );
}

/** The "New here? Create an account" line at the foot of each auth view. */
export function AuthSwitch({ prompt, to, action }: { prompt: string; to: string; action: string }) {
  return (
    <p className="text-subhead text-ink-secondary">
      {prompt}{' '}
      <Link
        to={to}
        className="font-semibold text-ink underline decoration-border decoration-2 underline-offset-4 transition-colors duration-150 hover:decoration-accent"
      >
        {action}
      </Link>
    </p>
  );
}
